import { useId, type ReactNode } from "react";
import { MAX_CONTENT_COLUMNS } from "@/lib/cms-puck/blocks/column-count";
import {
  gridChildrenAlignCss,
  gridLayout,
  resolveGridTrackSizes,
  type ColumnWidthWeight,
  type LayoutAlign,
  type LayoutGap,
} from "@/lib/cms-puck/layout";
import {
  gridPresetSpanCss,
  type GridPreset,
} from "@/lib/cms-puck/blocks/grid-presets";

type GridContainerProps = {
  columns: number;
  gap: LayoutGap;
  gapCustomPx?: number | null;
  gapRow?: LayoutGap | null;
  gapCol?: LayoutGap | null;
  gapRowCustomPx?: number | null;
  gapColCustomPx?: number | null;
  columnWidths?: ColumnWidthWeight[];
  verticalAlign?: LayoutAlign;
  preset?: GridPreset;
  children: ReactNode;
};

/**
 * Conteneur CSS Grid du bloc Grille — chaque enfant Puck devient une cellule.
 * PARITÉ front : inventozen-frontpublic/src/cms-puck/GridContainer.tsx
 */
export function GridContainer({
  columns,
  gap,
  gapCustomPx,
  gapRow,
  gapCol,
  gapRowCustomPx,
  gapColCustomPx,
  columnWidths,
  verticalAlign,
  preset,
  children,
}: GridContainerProps) {
  const align = verticalAlign ?? "stretch";
  const layout = gridLayout({
    columns,
    gap,
    gapCustomPx,
    gapRow,
    gapCol,
    gapRowCustomPx,
    gapColCustomPx,
    columnWidths,
    verticalAlign: align,
  });
  const n = Math.max(1, Math.min(MAX_CONTENT_COLUMNS, Math.floor(columns || 1)));
  const trackSizes = resolveGridTrackSizes(n, columnWidths);
  const uid = useId();
  const scopeClass = `cms-grd-${uid.replace(/[^a-zA-Z0-9_-]/g, "")}`;
  const presetCss = gridPresetSpanCss(scopeClass, preset ?? "custom");

  return (
    <>
      <style>
        {gridChildrenAlignCss(scopeClass, align, trackSizes)}
        {presetCss}
      </style>
      <div
        className={`${layout.className} ${scopeClass}`}
        style={layout.style}
      >
        {children}
      </div>
    </>
  );
}
